import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Card, ConfirmDialog, Icon, Money, PersonDot, StatusBadge } from './ui.jsx';
import { BalanceChart, ColumnChart } from './Charts.jsx';
import { monthLabel, dateLabel, addMonths, daysBetween } from '../lib/calendar.js';
import { confirmAllPending, markSetupDone } from '../lib/actions.js';
import { fmt } from '../lib/money.js';
import { buildModel } from '../lib/model.js';

export function setupItems(data) {
  const done = new Set(data.settings.setupDone || []);
  const items = [];
  for (const p of data.people.filter((x) => !x.until)) {
    const plan = p.plans[p.plans.length - 1];
    const hasIncome = plan && ((plan.mode === 'employee' && plan.base > 0) || (plan.mode === 'net' && plan.net > 0) || plan.mode === 'none');
    items.push({ key: `income:${p.id}`, title: `${p.name}: income`, text: plan?.mode === 'employee' ? 'Salary entered.' : 'Enter the take-home pay, or the full salary so taxes are worked out.', done: !!hasIncome || done.has(`income:${p.id}`), go: 'pay' });
    if (plan?.mode === 'employee' && !plan.startDate) items.push({ key: `start:${p.id}`, title: `${p.name}: first day at the job`, text: 'The first month is paid only from that day.', done: done.has(`start:${p.id}`), go: 'pay' });
    if (p.irsJovem?.enabled && !p.birthDate) items.push({ key: `birth:${p.id}`, title: `${p.name}: birth date`, text: 'IRS Jovem stops the year you turn 36.', done: done.has(`birth:${p.id}`), go: 'pay' });
    items.push({ key: `payday:${p.id}`, title: `${p.name}: payday`, text: 'When pay arrives each month; reviews open that day.', done: done.has(`payday:${p.id}`), go: 'pay', canTick: true });
    if (p.irsJovem?.enabled && p.irsJovem.atSource) {
      items.push({
        key: `hr:${p.id}`, title: `${p.name}: ask HR to apply IRS Jovem`, canTick: true, done: done.has(`hr:${p.id}`),
        text: `Without it IRS is withheld normally and only comes back in the yearly refund. Email HR: "Peço a aplicação do IRS Jovem na retenção na fonte (art. 99.º-F CIRS). O meu primeiro ano de obtenção de rendimentos é ${p.irsJovem.firstYear}."`,
      });
    }
  }
  items.push({ key: 'expenses', title: 'Joint expenses', text: 'Rent, bills, groceries… what you pay together each month.', done: data.expenses.length > 0 || done.has('expenses'), go: 'budget' });
  items.push({ key: 'allowance', title: 'Personal spending money', text: 'What each of you keeps for yourselves each month. Everything else is saved.', done: data.people.every((p) => p.allowance?.length) || done.has('allowance'), go: 'budget' });
  items.push({ key: 'opening', title: 'Savings you already have', text: 'Starting balance, so the total is right.', done: data.savings.opening !== 0 || done.has('opening'), go: 'savings', canTick: true });
  if (data.people.length > 1) items.push({ key: 'share', title: 'Give the others access', text: 'The app, or a spreadsheet if they prefer one.', done: done.has('share'), go: 'settings', canTick: true });
  return items;
}

export default function Overview() {
  const { data, model, commit, go, openReview, openMonth, me, setMe, today, person, sheets } = useApp();
  const [confirmAll, setConfirmAll] = useState(false);
  const items = setupItems(data);
  const hidden = (data.settings.setupDone || []).includes('hide');
  const todo = items.filter((i) => !i.done);

  const current = model.byKey[model.current];
  const chartPoints = useMemo(() => model.months
    .filter((m) => m.key >= addMonths(model.current, -18) && m.key <= addMonths(model.current, 12))
    .map((m) => ({ key: m.key, label: monthLabel(m.key), short: monthLabel(m.key, { short: true }).replace(' 20', " '"), value: m.balance, projected: m.key > model.current })), [model]);
  const savingsGroups = useMemo(() => model.months
    .filter((m) => m.key >= addMonths(model.current, -6) && m.key <= addMonths(model.current, 5))
    .map((m) => ({
      key: m.key, label: monthLabel(m.key), short: monthLabel(m.key, { short: true }).slice(0, 3), dim: m.key > model.current,
      values: { expected: m.expectedSavings, actual: m.key <= model.current ? m.savings : null },
    })), [model]);

  const goal = data.savings.goals[0];
  const next = model.nextPayday;
  const recent = [...(data.log || [])].reverse().slice(0, 8);

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>{me ? `Hi, ${person(me)?.name}` : 'Overview'}</h1>
          <p>{dateLabel(today)}</p>
        </div>
      </div>

      {!me && data.people.length > 0 && (
        <div className="banner info">
          <Icon name="user" className="icon" />
          <div className="stack tight" style={{ flex: 1 }}>
            <div>Who is using this device? It's used to show who confirmed what.</div>
            <div className="row wrap">
              {data.people.filter((p) => !p.until).map((p) => <button key={p.id} type="button" className="btn small" onClick={() => setMe(p.id)}><PersonDot color={p.color} />{p.name}</button>)}
            </div>
          </div>
        </div>
      )}

      {model.pending.length > 0 && (
        <section className="card" aria-label="Paydays to review" style={{ borderColor: 'rgba(250,178,25,0.35)' }}>
          <div className="card-head">
            <Icon name="alert" />
            <h2>{model.pending.length === 1 ? '1 payday to review' : `${model.pending.length} paydays to review`}</h2>
            <span className="spacer" />
            {model.pending.length > 1 && <button type="button" className="btn small" onClick={() => setConfirmAll(true)}>Confirm all as expected…</button>}
          </div>
          <p className="small muted" style={{ marginBottom: 8 }}>The expected amounts are already counted in savings. Check them against what really happened.</p>
          <div>
            {model.pending.slice(0, 6).map((e) => (
              <div className="list-row" key={`${e.month}:${e.personId}`}>
                <span className="pill-person"><PersonDot color={e.color} />{e.name}</span>
                <span className="dim">{monthLabel(e.month)}</span>
                <span className="small muted hide-sm">paid {dateLabel(e.payday, { weekday: false })}</span>
                <span className="spacer" />
                <span className="small muted">to savings</span> <Money value={e.actual.contribution} />
                <button type="button" className={`btn small ${e.personId === me || !me ? 'primary' : ''}`} aria-label={`Review ${e.name} ${monthLabel(e.month)}`} onClick={() => openReview(e.month, e.personId)}>Review</button>
              </div>
            ))}
            {model.pending.length > 6 && <button type="button" className="linkbtn small" onClick={() => go('months')}>…and {model.pending.length - 6} more in Months</button>}
          </div>
        </section>
      )}

      <div className="grid3">
        <div className="tile hero">
          <div className="label">Savings now</div>
          <div className="value num">{fmt(model.balance)}</div>
          <div className="sub">{model.pending.length ? 'includes paydays still to review' : 'all paydays reviewed'}</div>
        </div>
        <div className="tile">
          <div className="label">To savings in {monthLabel(model.current)}</div>
          <div className="value num">{current ? fmt(current.savings) : '—'}</div>
          <div className="sub">{current ? `expected ${fmt(current.expectedSavings)}` : 'before the first month'}</div>
        </div>
        <div className="tile">
          <div className="label">Next payday</div>
          <div className="value">{next ? dateLabel(next.date, { weekday: false }) : '—'}</div>
          <div className="sub">{next ? `${person(next.personId)?.name} · in ${daysBetween(today, next.date)} days` : 'nothing scheduled'}</div>
        </div>
        {goal && (
          <div className="tile">
            <div className="label">{goal.name}</div>
            <div className="value num">{Math.max(0, Math.min(100, Math.round((model.balance / goal.target) * 100)))}%</div>
            <div className="progress" style={{ marginTop: 8 }}><div style={{ width: `${Math.max(0, Math.min(100, (model.balance / goal.target) * 100))}%` }} /></div>
            <div className="sub">of {fmt(goal.target)}</div>
          </div>
        )}
      </div>

      {!hidden && todo.length > 0 && (
        <Card title={`Getting set up · ${items.length - todo.length} of ${items.length} done`} actions={<button type="button" className="btn ghost small" onClick={() => commit('Hide setup list', (s) => markSetupDone(s, 'hide'))}>Hide</button>}>
          <ul className="checklist">
            {items.map((i) => (
              <li key={i.key} className={i.done ? 'done' : ''}>
                <span className="tick" aria-hidden="true">{i.done && <Icon name="check" size={12} />}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="title" style={{ fontWeight: 560 }}>{i.title}</div>
                  {!i.done && <div className="small muted" style={{ overflowWrap: 'anywhere' }}>{i.text}</div>}
                </div>
                {!i.done && i.go && <button type="button" className="btn small" onClick={() => go(i.go)}>Open</button>}
                {i.canTick && (!i.done || (data.settings.setupDone || []).includes(i.key)) && <button type="button" className="btn ghost small" onClick={() => commit(i.done ? 'Undo setup step' : 'Setup step done', (s) => markSetupDone(s, i.key, !i.done))}>{i.done ? 'Not done' : 'Done'}</button>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="Savings balance">
          <BalanceChart points={chartPoints} />
        </Card>
        <Card title="Saved each month">
          <ColumnChart
            label="Saved each month, expected and actual"
            groups={savingsGroups}
            series={[{ id: 'expected', label: 'Expected', color: '#5d6168' }, { id: 'actual', label: 'Actual', color: 'var(--accent)' }]}
          />
        </Card>
      </div>

      <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="This month" actions={current && <button type="button" className="btn small" onClick={() => openMonth(current.key)}>Open month</button>}>
          {!current ? <p className="muted">Tracking starts in {monthLabel(data.settings.startMonth)}.</p> : (
            <div>
              {current.people.map((e) => (
                <div className="list-row" key={e.personId}>
                  <span className="pill-person"><PersonDot color={e.color} />{e.name}</span>
                  <StatusBadge status={e.status} source={e.source} />
                  <span className="spacer" />
                  <span className="small muted">take-home</span> <Money value={e.actual.net} />
                  <span className="small muted">to savings</span> <Money value={e.actual.contribution} />
                </div>
              ))}
              <div className="list-row"><span className="dim">Joint expenses budget</span><span className="spacer" /><Money value={current.jointBudget} /></div>
            </div>
          )}
        </Card>
        <Card title="Recent changes">
          {!recent.length ? <p className="muted">Nothing yet.</p> : (
            <div>
              {recent.map((l) => (
                <div className="list-row" key={l.id}>
                  {l.by && person(l.by) ? <PersonDot color={person(l.by).color} /> : <span className="swatch" style={{ background: 'var(--border-strong)', borderRadius: '50%' }} />}
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere', flex: 1 }}>{l.label}</span>
                  <span className="small muted nowrap">{new Date(l.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {confirmAll && (
        <ConfirmDialog
          title={`Confirm ${model.pending.length} paydays as expected?`}
          message="Each one is confirmed with the expected amounts. Use this to catch up when nothing unusual happened; you can reopen any of them later, or undo."
          confirmLabel="Confirm all"
          onCancel={() => setConfirmAll(false)}
          onConfirm={() => { commit(`Confirm ${model.pending.length} paydays`, (s) => confirmAllPending(s, buildModel(s, { today, sheets }), { by: me }), { undoToast: true }); setConfirmAll(false); }}
        />
      )}
    </div>
  );
}
